// Member pictures: the Google photo, or one of a set of icon avatars picked in Settings.
import {
  AnchorIcon, AtomIcon, BirdIcon, CandlestickChartIcon, CompassIcon, CrownIcon, DiamondIcon, FeatherIcon, FlameIcon,
  GaugeIcon, GemIcon, GlobeIcon, LeafIcon, MoonIcon, MountainIcon, OrbitIcon, RocketIcon, ShieldIcon, SparklesIcon,
  SunIcon, TargetIcon, TrendingUpIcon, WavesIcon, ZapIcon, type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Me } from '@/lib/client/use-me';

export const AVATARS: { id: string; icon: LucideIcon; from: string; to: string }[] = [
  { id: 'bull', icon: TrendingUpIcon, from: '#10b981', to: '#047857' },
  { id: 'candles', icon: CandlestickChartIcon, from: '#38bdf8', to: '#0369a1' },
  { id: 'target', icon: TargetIcon, from: '#fb7185', to: '#be123c' },
  { id: 'rocket', icon: RocketIcon, from: '#a78bfa', to: '#6d28d9' },
  { id: 'zap', icon: ZapIcon, from: '#fbbf24', to: '#d97706' },
  { id: 'flame', icon: FlameIcon, from: '#fb923c', to: '#c2410c' },
  { id: 'crown', icon: CrownIcon, from: '#facc15', to: '#a16207' },
  { id: 'gem', icon: GemIcon, from: '#22d3ee', to: '#0e7490' },
  { id: 'gauge', icon: GaugeIcon, from: '#60a5fa', to: '#1d4ed8' },
  { id: 'anchor', icon: AnchorIcon, from: '#3b82f6', to: '#1e3a8a' },
  { id: 'compass', icon: CompassIcon, from: '#2dd4bf', to: '#0f766e' },
  { id: 'mountain', icon: MountainIcon, from: '#4ade80', to: '#15803d' },
  { id: 'waves', icon: WavesIcon, from: '#67e8f9', to: '#0891b2' },
  { id: 'leaf', icon: LeafIcon, from: '#a3e635', to: '#4d7c0f' },
  { id: 'sun', icon: SunIcon, from: '#fde047', to: '#ea580c' },
  { id: 'moon', icon: MoonIcon, from: '#818cf8', to: '#3730a3' },
  { id: 'globe', icon: GlobeIcon, from: '#34d399', to: '#0f766e' },
  { id: 'shield', icon: ShieldIcon, from: '#94a3b8', to: '#334155' },
  { id: 'feather', icon: FeatherIcon, from: '#f472b6', to: '#9d174d' },
  { id: 'bird', icon: BirdIcon, from: '#7dd3fc', to: '#0284c7' },
  { id: 'diamond', icon: DiamondIcon, from: '#c084fc', to: '#7e22ce' },
  { id: 'atom', icon: AtomIcon, from: '#38bdf8', to: '#1e40af' },
  { id: 'orbit', icon: OrbitIcon, from: '#f9a8d4', to: '#6b21a8' },
  { id: 'sparkles', icon: SparklesIcon, from: '#fda4af', to: '#e11d48' },
];

const SIZE = { sm: 'size-8 text-[13px]', md: 'size-9 text-sm', lg: 'size-16 text-xl' } as const;
const ICON = { sm: 'size-4', md: 'size-4.5', lg: 'size-8' } as const;

export function AvatarIcon({ id, size = 'md', className }: { id: string; size?: keyof typeof SIZE; className?: string }) {
  const a = AVATARS.find((x) => x.id === id) ?? AVATARS[0];
  return (
    <span className={cn('grid flex-none place-items-center rounded-full text-white shadow-inner', SIZE[size], className)}
      style={{ background: `radial-gradient(circle at 30% 25%, ${a.from}, ${a.to})` }}>
      <a.icon className={ICON[size]} />
    </span>
  );
}

/** The member's chosen picture: icon avatar, Google photo ('google'), or their initial. */
export function UserAvatar({ me, size = 'md', className }: { me: Me | null; size?: keyof typeof SIZE; className?: string }) {
  if (me?.avatar && me.avatar !== 'google') return <AvatarIcon id={me.avatar} size={size} className={className} />;
  if (me?.picture && (me.avatar === 'google' || !me.avatar)) {
    return <img src={me.picture} alt="" referrerPolicy="no-referrer" className={cn('flex-none rounded-full object-cover', SIZE[size], className)} />;
  }
  return (
    <span className={cn('grid flex-none place-items-center rounded-full bg-brand-soft font-heading font-semibold text-brand-strong', SIZE[size], className)}>
      {(me?.displayName || me?.name || me?.user || 'M').slice(0, 1).toUpperCase()}
    </span>
  );
}
